import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export const usePropertyInvestment = (property: Tables<"real_estate_properties">) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showInvestDialog, setShowInvestDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleInvestment = async (type: 'full' | 'loan' | 'collateral', investmentAmount: string) => {
    try {
      setIsProcessing(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to invest in this property",
          variant: "destructive",
        });
        navigate("/signin");
        return;
      }

      const amount = parseFloat(investmentAmount);
      if (isNaN(amount) || amount <= 0) {
        toast({
          title: "Invalid Amount",
          description: "Please enter a valid investment amount",
          variant: "destructive",
        });
        return;
      }

      if (amount > property.price) {
        toast({
          title: "Invalid Amount",
          description: "Investment amount cannot exceed property price",
          variant: "destructive",
        });
        return;
      }

      const { data: collateral, error: collateralError } = await supabase
        .from('collateral_properties')
        .insert({
          borrower_id: user.id,
          property_id: property.id,
          collateral_value: amount,
          investment_type: type,
          investment_details: {
            property_title: property.title,
            investment_date: new Date().toISOString(),
            type: type
          }
        })
        .select()
        .single();

      if (collateralError) {
        throw collateralError;
      }

      const { error: updateError } = await supabase
        .from('real_estate_properties')
        .update({
          funding_progress: (property.funding_progress || 0) + amount
        })
        .eq('id', property.id);

      if (updateError) {
        throw updateError;
      }

      toast({
        title: "Investment Successful",
        description: `You have successfully invested ${amount} in ${property.title}`,
      });

      if (type === 'loan') {
        navigate(`/apply?property=${property.id}&collateral=${collateral.id}`);
      } else {
        setShowInvestDialog(false);
      }
    } catch (error: any) {
      console.error('Investment error:', error);
      toast({
        title: "Error",
        description: "Failed to process investment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    showInvestDialog,
    setShowInvestDialog,
    isProcessing,
    handleInvestment
  };
};